/**
 * Fechas de calendario local.
 *
 * REGLA DEL PROYECTO (design.md, sección 6):
 *
 *   Los días (entrenamiento, pesaje, comida, etapa, meta) se guardan como
 *   TEXTO 'AAAA-MM-DD', nunca como marca de tiempo.
 *
 * Motivo: una marca de tiempo lleva zona horaria implícita. `new Date('2026-10-12')`
 * se interpreta como UTC y en México puede resolverse al 11 de octubre. Eso
 * movería un día las etapas del plan, las comidas y los ayunos.
 *
 * Implementación: la aritmética interna usa Date.UTC, que es independiente de
 * la zona horaria y del horario de verano. Solo `hoy()` consulta el calendario
 * local del dispositivo, que es exactamente lo que debe hacer.
 *
 * Los instantes (creadoEn, inicio de un entrenamiento, una comida) sí usan ISO
 * completo, porque son momentos y no días de calendario; las horas viven en
 * domain/time.ts.
 *
 * Origen: Control de Negocio de Comida; los nombres ahora llevan tildes.
 */

import { ErrorValidacion } from './errors';

/** Fecha de calendario local en formato 'AAAA-MM-DD'. */
export type FechaLocal = string;

/** 0 = domingo ... 6 = sábado */
export type DiaSemana = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const MILISEGUNDOS_POR_DIA = 86_400_000;
const PATRON = /^(\d{4})-(\d{2})-(\d{2})$/;

// ---------------------------------------------------------------------------
// Validacion y construccion
// ---------------------------------------------------------------------------

/** Verifica el formato y que la fecha exista realmente en el calendario. */
export function esFechaLocal(valor: unknown): valor is FechaLocal {
  if (typeof valor !== 'string') return false;
  const coincidencia = PATRON.exec(valor);
  if (coincidencia === null) return false;

  const anio = Number(coincidencia[1]);
  const mes = Number(coincidencia[2]);
  const dia = Number(coincidencia[3]);
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return false;

  // Descarta fechas inexistentes como el 31 de febrero.
  const t = new Date(Date.UTC(anio, mes - 1, dia));
  return (
    t.getUTCFullYear() === anio && t.getUTCMonth() === mes - 1 && t.getUTCDate() === dia
  );
}

export function exigirFechaLocal(valor: unknown, campo = 'fecha'): FechaLocal {
  if (!esFechaLocal(valor)) {
    throw new ErrorValidacion(
      `El campo "${campo}" debe ser una fecha válida con formato AAAA-MM-DD.`,
      campo,
    );
  }
  return valor;
}

function rellenar(n: number): string {
  return String(n).padStart(2, '0');
}

/** Convierte una fecha local a milisegundos UTC para operar sin zona horaria. */
function aUtc(fecha: FechaLocal): number {
  const coincidencia = PATRON.exec(fecha);
  if (coincidencia === null) {
    throw new ErrorValidacion(`Fecha inválida: "${fecha}". Se espera AAAA-MM-DD.`);
  }
  return Date.UTC(
    Number(coincidencia[1]),
    Number(coincidencia[2]) - 1,
    Number(coincidencia[3]),
  );
}

function desdeUtc(milisegundos: number): FechaLocal {
  const d = new Date(milisegundos);
  return `${d.getUTCFullYear()}-${rellenar(d.getUTCMonth() + 1)}-${rellenar(d.getUTCDate())}`;
}

/**
 * Fecha de calendario de hoy segun el dispositivo.
 * Usa los captadores locales, no toISOString(), que devolveria la fecha UTC.
 */
export function hoy(): FechaLocal {
  const ahora = new Date();
  return `${ahora.getFullYear()}-${rellenar(ahora.getMonth() + 1)}-${rellenar(ahora.getDate())}`;
}

/** Convierte un objeto Date a fecha local, usando sus componentes locales. */
export function aFechaLocal(fecha: Date): FechaLocal {
  return `${fecha.getFullYear()}-${rellenar(fecha.getMonth() + 1)}-${rellenar(fecha.getDate())}`;
}

/** Marca de tiempo ISO completa. Para creadoEn y exportadoEn. */
export function ahoraIso(): string {
  return new Date().toISOString();
}

// ---------------------------------------------------------------------------
// Aritmetica
// ---------------------------------------------------------------------------

/**
 * Suma días a una fecha. Acepta valores negativos.
 *
 * Es la operación que sostiene el calendario de etapas del plan:
 *   fin = sumarDias(inicio, semanas × 7 − 1)
 */
export function sumarDias(fecha: FechaLocal, dias: number): FechaLocal {
  if (!Number.isInteger(dias)) {
    throw new ErrorValidacion('Los días a sumar deben ser un número entero.');
  }
  return desdeUtc(aUtc(fecha) + dias * MILISEGUNDOS_POR_DIA);
}

/** Dias que hay de `desde` a `hasta`. Positivo si `hasta` es posterior. */
export function diferenciaDias(desde: FechaLocal, hasta: FechaLocal): number {
  return Math.round((aUtc(hasta) - aUtc(desde)) / MILISEGUNDOS_POR_DIA);
}

export function esAnterior(a: FechaLocal, b: FechaLocal): boolean {
  return a < b; // el formato AAAA-MM-DD ordena igual como texto que como fecha
}

export function esPosterior(a: FechaLocal, b: FechaLocal): boolean {
  return a > b;
}

export function esHoy(fecha: FechaLocal): boolean {
  return fecha === hoy();
}

export function minima(a: FechaLocal, b: FechaLocal): FechaLocal {
  return a <= b ? a : b;
}

export function maxima(a: FechaLocal, b: FechaLocal): FechaLocal {
  return a >= b ? a : b;
}

/** Genera el rango inclusivo de fechas entre dos extremos. */
export function rangoDeFechas(desde: FechaLocal, hasta: FechaLocal): FechaLocal[] {
  const total = diferenciaDias(desde, hasta);
  if (total < 0) return [];
  const fechas: FechaLocal[] = [];
  for (let i = 0; i <= total; i++) fechas.push(sumarDias(desde, i));
  return fechas;
}

// ---------------------------------------------------------------------------
// Dias de la semana
// ---------------------------------------------------------------------------

export function diaSemana(fecha: FechaLocal): DiaSemana {
  return new Date(aUtc(fecha)).getUTCDay() as DiaSemana;
}

const NOMBRES_DIA: readonly string[] = [
  'domingo',
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
];

const NOMBRES_MES: readonly string[] = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/**
 * Nombre del dia de la semana. Se usa una tabla propia en lugar de Intl para
 * garantizar el mismo texto en todos los dispositivos y en las pruebas, sin
 * depender de la version de ICU del navegador.
 */
export function nombreDia(fecha: FechaLocal): string {
  return NOMBRES_DIA[diaSemana(fecha)] ?? '';
}

export function nombreDiaDeIndice(dia: DiaSemana): string {
  return NOMBRES_DIA[dia] ?? '';
}

export function nombreMes(fecha: FechaLocal): string {
  const coincidencia = PATRON.exec(fecha);
  if (coincidencia === null) return '';
  return NOMBRES_MES[Number(coincidencia[2]) - 1] ?? '';
}

export function anio(fecha: FechaLocal): number {
  return Number(PATRON.exec(fecha)?.[1] ?? 0);
}

export function diaDelMes(fecha: FechaLocal): number {
  return Number(PATRON.exec(fecha)?.[3] ?? 0);
}

// ---------------------------------------------------------------------------
// Presentacion
// ---------------------------------------------------------------------------

/** `"lunes 17 de agosto de 2026"` */
export function formatearLargo(fecha: FechaLocal): string {
  return `${nombreDia(fecha)} ${diaDelMes(fecha)} de ${nombreMes(fecha)} de ${anio(fecha)}`;
}

/** `"lunes 17 de agosto"` */
export function formatearMedio(fecha: FechaLocal): string {
  return `${nombreDia(fecha)} ${diaDelMes(fecha)} de ${nombreMes(fecha)}`;
}

/** `"17 ago"` */
export function formatearCorto(fecha: FechaLocal): string {
  return `${diaDelMes(fecha)} ${nombreMes(fecha).slice(0, 3)}`;
}

/** `"17 ago 2026"`, para calendarios que cruzan de año */
export function formatearCortoConAnio(fecha: FechaLocal): string {
  return `${formatearCorto(fecha)} ${anio(fecha)}`;
}

/**
 * Nombre relativo cuando la fecha está cerca de hoy, para que la pantalla Hoy
 * diga "mañana, martes" en lugar de una fecha que hay que interpretar (Req. 24).
 */
export function nombreRelativo(fecha: FechaLocal, referencia: FechaLocal = hoy()): string {
  const dias = diferenciaDias(referencia, fecha);
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'mañana';
  if (dias === -1) return 'ayer';
  if (dias === 2) return 'pasado mañana';
  if (dias > 0 && dias <= 6) return nombreDia(fecha);
  return formatearMedio(fecha);
}

/** `"mañana, martes"` o `"hoy"` */
export function nombreRelativoConDia(fecha: FechaLocal, referencia: FechaLocal = hoy()): string {
  const dias = diferenciaDias(referencia, fecha);
  if (dias === 0) return 'hoy';
  if (dias === 1) return `mañana, ${nombreDia(fecha)}`;
  if (dias === -1) return `ayer, ${nombreDia(fecha)}`;
  if (dias > 0 && dias <= 6) return nombreDia(fecha);
  return formatearMedio(fecha);
}

// ---------------------------------------------------------------------------
// Semanas y meses
// ---------------------------------------------------------------------------

/**
 * Primer dia de la semana que contiene la fecha.
 * `primerDia` es 1 para semanas que empiezan en lunes, 0 para domingo.
 */
export function inicioSemana(fecha: FechaLocal, primerDia: 0 | 1 = 1): FechaLocal {
  const actual = diaSemana(fecha);
  const desplazamiento = (actual - primerDia + 7) % 7;
  return sumarDias(fecha, -desplazamiento);
}

export function finSemana(fecha: FechaLocal, primerDia: 0 | 1 = 1): FechaLocal {
  return sumarDias(inicioSemana(fecha, primerDia), 6);
}

export function rangoSemana(
  fecha: FechaLocal,
  primerDia: 0 | 1 = 1,
): { desde: FechaLocal; hasta: FechaLocal } {
  const desde = inicioSemana(fecha, primerDia);
  return { desde, hasta: sumarDias(desde, 6) };
}

/** Los siete dias de la semana que contiene la fecha. */
export function diasDeSemana(fecha: FechaLocal, primerDia: 0 | 1 = 1): FechaLocal[] {
  const desde = inicioSemana(fecha, primerDia);
  return Array.from({ length: 7 }, (_, i) => sumarDias(desde, i));
}

export function inicioMes(fecha: FechaLocal): FechaLocal {
  const coincidencia = PATRON.exec(fecha);
  if (coincidencia === null) throw new ErrorValidacion(`Fecha inválida: "${fecha}".`);
  return `${coincidencia[1]}-${coincidencia[2]}-01`;
}

export function finMes(fecha: FechaLocal): FechaLocal {
  const coincidencia = PATRON.exec(fecha);
  if (coincidencia === null) throw new ErrorValidacion(`Fecha inválida: "${fecha}".`);
  // El dia 0 del mes siguiente es el ultimo del mes actual.
  const ultimo = new Date(Date.UTC(Number(coincidencia[1]), Number(coincidencia[2]), 0));
  return desdeUtc(ultimo.getTime());
}

export function rangoMes(fecha: FechaLocal): { desde: FechaLocal; hasta: FechaLocal } {
  return { desde: inicioMes(fecha), hasta: finMes(fecha) };
}

/** `"agosto 2026"` */
export function etiquetaMes(fecha: FechaLocal): string {
  return `${nombreMes(fecha)} ${anio(fecha)}`;
}

export function sumarMeses(fecha: FechaLocal, meses: number): FechaLocal {
  const coincidencia = PATRON.exec(fecha);
  if (coincidencia === null) throw new ErrorValidacion(`Fecha inválida: "${fecha}".`);
  const anioActual = Number(coincidencia[1]);
  const mesActual = Number(coincidencia[2]) - 1;
  const diaActual = Number(coincidencia[3]);

  const total = anioActual * 12 + mesActual + meses;
  const nuevoAnio = Math.floor(total / 12);
  const nuevoMes = total % 12;

  // Ajusta al ultimo dia disponible: 31 de enero mas un mes da 28 o 29 de febrero.
  const ultimoDia = new Date(Date.UTC(nuevoAnio, nuevoMes + 1, 0)).getUTCDate();
  return desdeUtc(Date.UTC(nuevoAnio, nuevoMes, Math.min(diaActual, ultimoDia)));
}

/**
 * Numero de semana dentro del mes, contando desde 1.
 * Sirve para el desglose mensual por semanas del Requisito 16.2.
 */
export function semanaDelMes(fecha: FechaLocal, primerDia: 0 | 1 = 1): number {
  const inicioDelMes = inicioMes(fecha);
  const inicioSemanaDelMes = inicioSemana(inicioDelMes, primerDia);
  const inicioSemanaActual = inicioSemana(fecha, primerDia);
  return diferenciaDias(inicioSemanaDelMes, inicioSemanaActual) / 7 + 1;
}
